import React, { useState, useEffect, useContext } from "react";
import { getAllUsers, createUser, updateUser, getUserById } from "../../business/UserBusiness";
import { AuthContext } from "../../context/Auth";
import { Search, Plus, Eye, Pencil, Crown, Trash2, Mail, MessageCircle } from "lucide-react";
import { Sidebar } from '../../components/Sidebar';
import { EnterprisePlanModal } from '../../components/EnterprisePlanModal';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Badge, PlanBadge } from '../../components/ui/Badge';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { PLAN_META, getEffectivePlan, getPlanSourceLabel } from '../../lib/planMeta';
import { updatePlanOverride, adminDeleteUser, updateDispatchConfig, createAmbassador } from "../../business/AdminBusiness";
import { CheckboxField } from "../../components/ui/CheckboxField";
import { normalizeReferralCode, isValidReferralCode } from "../../lib/referralCode";
import { ensureCountryCode } from "../../lib/phone";
import { canDeleteUsers } from "../../lib/protectedAccounts";
import toast from "react-hot-toast";

const inputClass = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";
const primaryButtonClass = "inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-60 flex-1";
const secondaryButtonClass = "rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex-1";

// Controla o prazo de um planOverride manual (dias) — usado tanto na criação quanto na edição
// de usuário, sempre junto do select de plano. Vencido o prazo, o override some sozinho
// (getEffectivePlan ignora e cai pro plano real) — não precisa vir aqui apagar nada.
const PlanOverrideDurationPicker = ({ expiresAt, onChange }) => {
  const daysRemaining = expiresAt
    ? Math.max(1, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000))
    : '';

  const setDays = (days) => {
    if (!days) {
      onChange(null);
      return;
    }
    const next = new Date();
    next.setDate(next.getDate() + Number(days));
    onChange(next.toISOString());
  };

  return (
    <div className="mt-3">
      <label className={labelClass}>Duração do plano manual</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {[{ label: '1 semana', days: 7 }, { label: '1 mês', days: 30 }, { label: '1 ano', days: 365 }].map((opt) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => setDays(opt.days)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(null)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          Sem prazo
        </button>
      </div>
      <input
        type="number"
        min="1"
        placeholder="Dias personalizados"
        value={daysRemaining}
        onChange={(e) => setDays(e.target.value)}
        className={inputClass}
      />
      <p className="text-xs text-gray-400 mt-1">
        {expiresAt
          ? `Expira em ${new Date(expiresAt).toLocaleDateString('pt-BR')} — depois disso volta pro plano real (assinatura/RevenueCat/Kiwify) automaticamente.`
          : 'Sem prazo definido: o plano manual vale até um admin trocar.'}
      </p>
    </div>
  );
};

export const Home = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [enterpriseConfigUser, setEnterpriseConfigUser] = useState(null)
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 'user',
    planOverride: '',
    planOverrideExpiresAt: null,
    dispatchesDisabled: false,
    isAmbassador: false,
    ambassadorCode: ''
  })
  const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt', 'username', 'transactions'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' ou 'desc'

  const adminName = localStorage.getItem("@Auth:name")
  const { user } = useContext(AuthContext);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await getAllUsers();
      setUsers(response?.data || []);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const confirmDeleteUser = async (userId) => {
    try {
      await adminDeleteUser(userId);
      toast.success('Usuário foi removido com sucesso!');
      setDeleteTarget(null);
      setSelectedPerson(null);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir conta do usuário:', error);
      toast.error('Erro ao deletar conta de usuário')
    }
  };

  const isProtected = (person) => person.email === 'alexandresaintss@gmail.com' && user?.email !== 'alexandresaintss@gmail.com';
  const canDelete = canDeleteUsers(user?.email);

  const filteredUsers = users?.filter((person) => {
    const matchesSearch = person?.username?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                         person?.email?.toLowerCase().includes(searchTerm?.toLowerCase());

    const matchesRole = roleFilter === "all" || person?.role === roleFilter;
    const matchesPlan = planFilter === "all" || getEffectivePlan(person) === planFilter;

    return matchesSearch && matchesRole && matchesPlan;
  });

  // Ordenar usuários
  const sortedUsers = filteredUsers?.sort((a, b) => {
    if (sortBy === 'username') {
      const nameA = a.username?.toLowerCase() || '';
      const nameB = b.username?.toLowerCase() || '';
      return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    } else if (sortBy === 'createdAt') {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortBy === 'transactions') {
      const transactionsA = a.transactions?.length || 0;
      const transactionsB = b.transactions?.length || 0;
      return sortOrder === 'asc' ? transactionsA - transactionsB : transactionsB - transactionsA;
    }
    return 0;
  });

  const handleSearchTerm = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleFilter = (e) => {
    setRoleFilter(e.target.value);
  };

  const handlePlanFilter = (e) => {
    setPlanFilter(e.target.value);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const handlePersonClick = async (person) => {
    try {
      setIsLoadingUserDetails(true);
      setSelectedPerson(person);

      const response = await getUserById(person?._id);

      if (response?.data?.user) {
        setSelectedPerson({
          ...person,
          ...response.data.user,
          clients: response.data.user.clients || person?.clients || [],
          employees: response.data.user.employees || person?.employees || []
        });
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do usuário:', error);
      toast.error('Erro ao carregar detalhes do usuário');
    } finally {
      setIsLoadingUserDetails(false);
    }
  };

  const handleEditClick = (person) => {
    setEditingUser({
      _id: person._id,
      username: person.username,
      email: person.email || '',
      phone: person.phone || '',
      role: person.role || 'user',
      subscription: person.subscription || 'inactive',
      planOverride: person.planOverride ?? '',
      planOverrideExpiresAt: person.planOverrideExpiresAt ?? null,
      dispatchesDisabled: person.dispatchesDisabled === true,
      originalDispatchesDisabled: person.dispatchesDisabled === true
    });
    setShowEditModal(true);
  };

  const handleModalClose = () => {
    setSelectedPerson(null);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleEditUser = async () => {
    try {
      if (!editingUser.email || !editingUser.phone) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      setIsLoading(true);

      const updateData = {
        email: editingUser.email,
        phone: editingUser.phone,
        role: editingUser.role,
        subscription: editingUser.subscription
      };

      await updateUser(editingUser._id, updateData);

      const planOverrideValue = editingUser.planOverride === '' ? null : editingUser.planOverride;
      await updatePlanOverride(editingUser._id, planOverrideValue, editingUser.planOverrideExpiresAt);
      if (editingUser.dispatchesDisabled !== editingUser.originalDispatchesDisabled) {
        await updateDispatchConfig(editingUser._id, editingUser.dispatchesDisabled);
      }
      toast.success('Usuário atualizado com sucesso!');

      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      toast.error(error?.response?.data?.msg || 'Erro ao atualizar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditingUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCreatedAt = (createdAt) => {
    const createdAtDate = new Date(createdAt);
    return createdAtDate.toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'admin': return 'Administrador';
      case 'user': return 'Usuário';
      case 'patner': return 'Parceiro';
      case 'trial': return 'Trial';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'user': return 'bg-blue-100 text-blue-700';
      case 'patner': return 'bg-green-100 text-green-700';
      case 'trial': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getInitials = (username) => {
    if (!username) return '?';
    const names = username.trim().split(' ');
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const getAvatarColor = (username) => {
    if (!username) return 'bg-gray-500';
    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'];
    const index = username.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.username || !newUser.email || !newUser.phone || !newUser.password) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }
      if (newUser.isAmbassador && !isValidReferralCode(newUser.ambassadorCode)) {
        toast.error('Código da embaixadora inválido: use de 3 a 20 letras ou números.');
        return;
      }

      setIsLoading(true);
      const { dispatchesDisabled, isAmbassador, ambassadorCode, ...registerPayload } = newUser;
      const res = await createUser({ ...registerPayload, phone: ensureCountryCode(newUser.phone) });
      const createdId = res?.data?.user?._id || res?.data?._id;
      if (createdId && newUser.planOverride) {
        await updatePlanOverride(createdId, newUser.planOverride, newUser.planOverrideExpiresAt);
      }

      // Embaixadora: um endpoint só já grava o código e o "sem disparos". Se falhar (ex.: código
      // repetido) a conta já existe — avisa e manda cadastrar pela tela Embaixadoras.
      let ambassadorFailure = null;
      if (createdId && isAmbassador) {
        try {
          await createAmbassador({ userId: createdId, code: ambassadorCode, disableDispatches: dispatchesDisabled });
        } catch (ambassadorError) {
          ambassadorFailure = ambassadorError?.response?.data?.msg || 'erro ao cadastrar a embaixadora';
        }
      } else if (createdId && dispatchesDisabled) {
        await updateDispatchConfig(createdId, true);
      }

      if (ambassadorFailure) {
        toast.error(`Usuário criado, mas a embaixadora não foi cadastrada: ${ambassadorFailure}. Cadastre pela tela Embaixadoras.`, { duration: 8000 });
      } else {
        toast.success('Usuário criado com sucesso!');
      }

      setShowCreateModal(false);
      setNewUser({
        username: '',
        email: '',
        phone: '',
        password: '',
        role: 'user',
        planOverride: '',
        planOverrideExpiresAt: null,
        dispatchesDisabled: false,
        isAmbassador: false,
        ambassadorCode: ''
      });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error(error?.response?.data?.msg || 'Erro ao criar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const rowActionsFor = (person) => {
    const blocked = isProtected(person);
    const blockedTitle = 'Somente o próprio Alexandre pode gerenciar esta conta';
    const actions = [
      { key: 'view', label: 'Ver detalhes', icon: Eye, onClick: () => handlePersonClick(person) },
      { key: 'edit', label: 'Editar', icon: Pencil, onClick: () => handleEditClick(person), disabled: blocked, disabledTitle: blockedTitle },
      { key: 'enterprise', label: 'Configurar Enterprise', icon: Crown, onClick: () => setEnterpriseConfigUser(person), disabled: blocked, disabledTitle: blockedTitle },
    ];
    // Excluir só aparece pra quem tem permissão — a trava de verdade é no backend
    // (requireUserDeletePermission), isso aqui é só pra não oferecer um botão que vai 403.
    if (canDelete) {
      actions.push({ key: 'delete', label: 'Excluir', icon: Trash2, danger: true, onClick: () => setDeleteTarget(person), disabled: blocked, disabledTitle: blockedTitle });
    }
    return actions;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <>
      <div className="flex flex-col lg:flex-row bg-gray-50 min-h-screen">
        <Sidebar />
        <div className="flex-1 min-h-screen">
          <div className="max-w-7xl w-full mx-auto p-3 sm:p-6 space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Olá, {adminName}</h1>
              <p className="text-sm text-gray-500">Dashboard — Todos os usuários ({sortedUsers?.length || 0})</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Buscar por username ou email..."
                    onChange={handleSearchTerm}
                    value={searchTerm}
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={handleRoleFilter}
                  className="rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent sm:w-48"
                >
                  <option value="all">Todos os cargos</option>
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                  <option value="patner">Parceiro</option>
                  <option value="trial">Trial</option>
                </select>
                <select
                  value={planFilter}
                  onChange={handlePlanFilter}
                  className="rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent sm:w-48"
                >
                  <option value="all">Todos os planos</option>
                  {Object.keys(PLAN_META).map((plan) => (
                    <option key={plan} value={plan}>{PLAN_META[plan]?.label || plan}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
              >
                <Plus className="h-4 w-4" /> Criar usuário
              </button>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSortChange('username')}
                      >
                        <div className="flex items-center">
                          Username
                          {sortBy === 'username' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden md:table-cell">Email</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden lg:table-cell">Telefone</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Cargo</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden lg:table-cell">Plano</th>
                      <th
                        className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden xl:table-cell cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSortChange('createdAt')}
                      >
                        <div className="flex items-center">
                          Criado em
                          {sortBy === 'createdAt' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                        </div>
                      </th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {isLoading ? (
                      <tr><td colSpan="7" className="py-8 px-5 text-center text-sm text-gray-400">Carregando...</td></tr>
                    ) : sortedUsers && sortedUsers.length > 0 ? (
                      sortedUsers.map((person) => (
                        <tr
                          key={person?.email}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handlePersonClick(person)}
                        >
                          <td className="whitespace-nowrap px-5 py-3.5">
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${getAvatarColor(person?.username)}`}>
                                {getInitials(person?.username)}
                              </div>
                              <div className="ml-3 text-sm font-medium text-gray-900 truncate max-w-[120px]">{person?.username}</div>
                              {person?.isAmbassador === true && <Badge className="ml-2 bg-purple-100 text-purple-700 normal-case">Embaixadora</Badge>}
                              {person?.dispatchesDisabled === true && <Badge className="ml-2 bg-amber-100 text-amber-700 normal-case">Sem disparos</Badge>}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 hidden md:table-cell">
                            <div className="text-sm text-gray-500 truncate max-w-[180px]">{person?.email}</div>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 hidden lg:table-cell text-sm text-gray-500">{person?.phone}</td>
                          <td className="whitespace-nowrap px-5 py-3.5">
                            <Badge className={getRoleColor(person?.role)}>{getRoleLabel(person?.role)}</Badge>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 hidden lg:table-cell">
                            <PlanBadge plan={getEffectivePlan(person)} meta={PLAN_META} />
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 hidden xl:table-cell text-sm text-gray-500">{formatCreatedAt(person?.createdAt)}</td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <RowActionsMenu actions={rowActionsFor(person)} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="7" className="py-8 px-5 text-center text-sm text-gray-400">Nenhum usuário encontrado</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Modal de criação de usuário */}
          <Modal title="Criar Novo Usuário" isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
            <div className="space-y-4">
              <div>
                <label className={labelClass} htmlFor="username">Nome *</label>
                <input className={inputClass} id="username" type="text" placeholder="joao_silva" value={newUser.username} onChange={(e) => handleInputChange('username', e.target.value)} />
              </div>
              <div>
                <label className={labelClass} htmlFor="email">Email *</label>
                <input className={inputClass} id="email" type="email" placeholder="joao@email.com" value={newUser.email} onChange={(e) => handleInputChange('email', e.target.value)} />
              </div>
              <div>
                <label className={labelClass} htmlFor="phone">Telefone *</label>
                <input className={inputClass} id="phone" type="tel" placeholder="(11) 99999-9999" value={newUser.phone} onChange={(e) => handleInputChange('phone', e.target.value)} />
                <p className="text-xs text-gray-400 mt-1">Lembre-se do código do país na frente</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="password">Senha *</label>
                <input className={inputClass} id="password" type="password" placeholder="********" value={newUser.password} onChange={(e) => handleInputChange('password', e.target.value)} />
              </div>
              <div>
                <label className={labelClass} htmlFor="role">Cargo *</label>
                <select className={inputClass} id="role" value={newUser.role} onChange={(e) => handleInputChange('role', e.target.value)}>
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                  <option value="patner">Parceiro</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="create-plan-override">Plano manual</label>
                <select className={inputClass} id="create-plan-override" value={newUser.planOverride} onChange={(e) => handleInputChange('planOverride', e.target.value)}>
                  <option value="">Free (padrão)</option>
                  <option value="start">Start</option>
                  <option value="pro">Pro</option>
                  <option value="studio">Studio</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Sobrescreve o plano padrão do usuário para testes ou acessos personalizados.</p>
                {newUser.planOverride && (
                  <PlanOverrideDurationPicker
                    expiresAt={newUser.planOverrideExpiresAt}
                    onChange={(value) => handleInputChange('planOverrideExpiresAt', value)}
                  />
                )}
              </div>
              <div className="space-y-2">
                <CheckboxField
                  id="create-dispatches-disabled"
                  checked={newUser.dispatchesDisabled}
                  onChange={(value) => handleInputChange('dispatchesDisabled', value)}
                  label="Sem disparos (só push)"
                  help="Não envia WhatsApp nem e-mail (confirmações, lembretes, aniversário, relatórios). O push do app continua. Por padrão toda conta tem disparos."
                />
                <CheckboxField
                  id="create-is-ambassador"
                  checked={newUser.isAmbassador}
                  onChange={(value) => {
                    handleInputChange('isAmbassador', value);
                    // Embaixadora nasce sem disparos (decisão da call de 23/09) — dá pra desmarcar.
                    if (value) handleInputChange('dispatchesDisabled', true);
                  }}
                  label="É embaixadora"
                  help="Cadastra a conta no programa de embaixadoras com o código de indicação abaixo."
                />
                {newUser.isAmbassador && (
                  <div>
                    <label className={labelClass} htmlFor="create-ambassador-code">Código de indicação *</label>
                    <input
                      className={`${inputClass} uppercase`}
                      id="create-ambassador-code"
                      type="text"
                      placeholder="GABI10"
                      maxLength={20}
                      value={newUser.ambassadorCode}
                      onChange={(e) => handleInputChange('ambassadorCode', normalizeReferralCode(e.target.value))}
                    />
                    <p className="text-xs text-gray-400 mt-1">3 a 20 letras ou números, sem espaços. Sempre em maiúsculas e único por embaixadora.</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <button className={primaryButtonClass} onClick={handleCreateUser} disabled={isLoading}>
                  {isLoading ? 'Criando...' : 'Criar Usuário'}
                </button>
                <button className={secondaryButtonClass} onClick={() => setShowCreateModal(false)} disabled={isLoading}>
                  Cancelar
                </button>
              </div>
            </div>
          </Modal>

          {/* Modal de edição de usuário */}
          <Modal title="Editar Usuário" isOpen={showEditModal && !!editingUser} onClose={handleEditModalClose}>
            {editingUser && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass} htmlFor="edit-username">Nome</label>
                  <input className={`${inputClass} bg-gray-100`} id="edit-username" type="text" value={editingUser.username} disabled />
                  <p className="text-xs text-gray-400 mt-1">Username não pode ser alterado</p>
                </div>
                <div>
                  <label className={labelClass} htmlFor="edit-email">Email *</label>
                  <input className={inputClass} id="edit-email" type="email" placeholder="joao@email.com" value={editingUser.email} onChange={(e) => handleEditInputChange('email', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="edit-phone">Telefone *</label>
                  <input className={inputClass} id="edit-phone" type="tel" placeholder="(11) 99999-9999" value={editingUser.phone} onChange={(e) => handleEditInputChange('phone', e.target.value)} />
                  <p className="text-xs text-gray-400 mt-1">Lembre-se do código do país na frente</p>
                </div>
                <div>
                  <label className={labelClass} htmlFor="edit-role">Cargo *</label>
                  <select className={inputClass} id="edit-role" value={editingUser.role} onChange={(e) => handleEditInputChange('role', e.target.value)}>
                    <option value="user">Usuário</option>
                    <option value="admin">Administrador</option>
                    <option value="patner">Parceiro</option>
                    <option value="trial">Trial</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="edit-subscription">Status de assinatura *</label>
                  <select className={inputClass} id="edit-subscription" value={editingUser.subscription} onChange={(e) => handleEditInputChange('subscription', e.target.value)}>
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="edit-plan-override">Plano manual</label>
                  <select className={inputClass} id="edit-plan-override" value={editingUser.planOverride ?? ''} onChange={(e) => handleEditInputChange('planOverride', e.target.value)}>
                    <option value="">Nenhum (padrão pelo plano contratado)</option>
                    <option value="free">Free</option>
                    <option value="start">Start</option>
                    <option value="pro">Pro</option>
                    <option value="studio">Studio</option>
                    <option value="business">Business</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Sobrescreve o plano atual do usuário para testes ou acessos personalizados.</p>
                  {editingUser.planOverride && (
                    <PlanOverrideDurationPicker
                      expiresAt={editingUser.planOverrideExpiresAt}
                      onChange={(value) => handleEditInputChange('planOverrideExpiresAt', value)}
                    />
                  )}
                </div>
                <CheckboxField
                  id="edit-dispatches-disabled"
                  checked={editingUser.dispatchesDisabled === true}
                  onChange={(value) => handleEditInputChange('dispatchesDisabled', value)}
                  label="Sem disparos (só push)"
                  help="Não envia WhatsApp nem e-mail (confirmações, lembretes, aniversário, relatórios). O push do app continua. Cuidado ao marcar em conta de cliente pagante."
                />
                <div className="flex gap-2 pt-2">
                  <button className={primaryButtonClass} onClick={handleEditUser} disabled={isLoading}>
                    {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                  <button className={secondaryButtonClass} onClick={handleEditModalClose} disabled={isLoading}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </Modal>

          {/* Modal de configuração do plano enterprise */}
          {enterpriseConfigUser && (
            <EnterprisePlanModal
              user={enterpriseConfigUser}
              onClose={() => setEnterpriseConfigUser(null)}
              onSaved={(updatedUser) => {
                if (updatedUser) {
                  setUsers((prev) => prev.map((u) => (u._id === updatedUser._id ? updatedUser : u)));
                }
                setEnterpriseConfigUser(null);
              }}
            />
          )}

          {/* Modal de visualização */}
          <Modal title="Detalhes do Usuário" isOpen={!!selectedPerson} onClose={handleModalClose}>
            {isLoadingUserDetails ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                <span className="ml-3 text-gray-500 text-sm">Carregando detalhes...</span>
              </div>
            ) : selectedPerson && (
              <>
                <div className="flex items-center mb-6">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center text-white font-semibold text-xl ${getAvatarColor(selectedPerson?.username)}`}>
                    {getInitials(selectedPerson?.username)}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-base font-semibold text-gray-900">{selectedPerson?.username}</h3>
                    <Badge className={getRoleColor(selectedPerson?.role)}>{getRoleLabel(selectedPerson?.role)}</Badge>
                  </div>
                </div>

                <div className="mb-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Informações de Contato</h4>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Email:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 break-all">{selectedPerson?.email}</span>
                        {selectedPerson?.email && (
                          <a
                            href={`mailto:${selectedPerson.email}`}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            title="Enviar e-mail"
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Telefone:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{selectedPerson?.phone}</span>
                        {selectedPerson?.phone && (
                          <a
                            href={`https://wa.me/${String(selectedPerson.phone).replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-green-600"
                            title="Abrir WhatsApp"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Informações do Negócio</h4>
                  <div className="space-y-1.5">
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Nome do Negócio:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.businessName || <span className="text-gray-400">Não informado</span>}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Receita Média:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.averageRevenue != null ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedPerson.averageRevenue) : <span className="text-gray-400">Não informado</span>}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Quantidade de Clientes:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.clients?.length || 0}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Quantidade de Funcionários:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.employees?.length || 0}</span></div>
                  </div>
                </div>

                {getEffectivePlan(selectedPerson) === 'enterprise' && (
                  <div className="mb-5">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Configuração Enterprise</h4>
                    <div className="space-y-1.5">
                      {[
                        { key: 'maxAppointmentsPerMonth', label: 'Agendamentos/mês' },
                        { key: 'maxClients', label: 'Clientes' },
                        { key: 'maxEmployees', label: 'Funcionários' },
                        { key: 'maxSharedAccounts', label: 'Usuários e acessos' },
                      ].map(({ key, label }) => (
                        <div className="flex justify-between" key={key}>
                          <span className="text-sm text-gray-500">{label}:</span>
                          <span className="text-sm font-medium text-gray-900">{selectedPerson?.enterpriseLimits?.[key] ?? 'Ilimitado'}</span>
                        </div>
                      ))}
                      <div className="flex justify-between"><span className="text-sm text-gray-500">Valor mensal:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.enterprisePricing?.monthlyPrice != null ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedPerson.enterprisePricing.monthlyPrice) : <span className="text-gray-400">Não informado</span>}</span></div>
                      <div className="flex justify-between"><span className="text-sm text-gray-500">Valor anual:</span><span className="text-sm font-medium text-gray-900">{selectedPerson?.enterprisePricing?.annualPrice != null ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedPerson.enterprisePricing.annualPrice) : <span className="text-gray-400">Não informado</span>}</span></div>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Status e Sistema</h4>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center"><span className="text-sm text-gray-500">Plano:</span><div className="flex flex-col items-end gap-0.5"><PlanBadge plan={getEffectivePlan(selectedPerson)} meta={PLAN_META} /><span className="text-[11px] text-gray-400">{getPlanSourceLabel(selectedPerson)}</span></div></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Assinatura real:</span><span className={`text-sm font-medium ${selectedPerson?.subscription === 'active' ? 'text-green-600' : 'text-red-600'}`}>{selectedPerson?.subscription === 'active' ? 'Ativo' : 'Inativo'}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">Criado em:</span><span className="text-sm font-medium text-gray-900">{formatCreatedAt(selectedPerson?.createdAt)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-500">ID do usuário:</span><span className="text-sm font-medium text-gray-400 break-all">{selectedPerson?._id}</span></div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button className={primaryButtonClass} onClick={() => { handleModalClose(); handleEditClick(selectedPerson); }}>
                    Editar Usuário
                  </button>
                  {canDelete && (
                    <button
                      className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                      onClick={() => setDeleteTarget(selectedPerson)}
                    >
                      Excluir
                    </button>
                  )}
                </div>
              </>
            )}
          </Modal>

          <ConfirmModal
            isOpen={!!deleteTarget}
            title="Excluir usuário"
            message={`Tem certeza que deseja excluir "${deleteTarget?.username}"? Essa ação não pode ser desfeita.`}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={() => confirmDeleteUser(deleteTarget?._id)}
          />
        </div>
      </div>
    </>
  );
};
